import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [ctaEmail, setCtaEmail] = useState('');

  const handleGetStarted = (e) => {
    e.preventDefault();
    if (email) {
      // Store email for signup page
      localStorage.setItem('signupEmail', email);
      navigate('/account?mode=signup');
    } else {
      navigate('/account?mode=signup');
    }
  };

  const handleCtaSubmit = (e) => {
    e.preventDefault();
    if (ctaEmail) {
      localStorage.setItem('signupEmail', ctaEmail);
      navigate('/account?mode=signup');
    } else {
      navigate('/account?mode=signup');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>LoraTag - AI-Powered Image Captioning for LoRA Training | Batch Dataset Preparation Tool</title>
        <meta name="description" content="Caption hundreds of images for LoRA training in minutes. LoraTag automates dataset preparation with customizable detail levels, batch processing, and directory support. Try free." />
        <link rel="canonical" href="https://loratag.ai/" />

        {/* Open Graph */}
        <meta property="og:title" content="LoraTag - AI Caption Generator for LoRA Training" />
        <meta property="og:description" content="Caption hundreds of images for LoRA training in minutes. Automates dataset preparation with batch processing and customizable detail levels. Try free." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://loratag.ai/" />
        <meta property="og:image" content="https://loratag.ai/og-image.png" />
        <meta property="og:site_name" content="LoraTag" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="LoraTag - AI Caption Generator for LoRA Training" />
        <meta name="twitter:description" content="Caption hundreds of images for LoRA training in minutes. Try free." />
        <meta name="twitter:image" content="https://loratag.ai/og-image.png" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "LoraTag",
            "description": "AI-powered image captioning tool for LoRA training datasets. Batch process hundreds of images with customizable detail levels.",
            "url": "https://loratag.ai",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "AggregateOffer",
              "lowPrice": "0",
              "highPrice": "24.99",
              "priceCurrency": "USD",
              "offerCount": "5"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "1"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What file formats are supported?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We support all common image formats including JPG, PNG, WebP, GIF, and BMP. Files up to 10MB each (higher limits on paid plans)."
                }
              },
              {
                "@type": "Question",
                "name": "How accurate are the captions?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Our OpenAI Vision integration provides highly accurate descriptions. The AI identifies characters, poses, clothing, environments, and artistic styles with impressive precision."
                }
              },
              {
                "@type": "Question",
                "name": "Can I edit captions after generation?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes! Every caption can be edited before download. You can also regenerate individual captions if needed."
                }
              },
              {
                "@type": "Question",
                "name": "What's a LoRA?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "LoRA (Low-Rank Adaptation) is a training technique for customizing AI image models. It requires a dataset of images with detailed captions describing their contents."
                }
              },
              {
                "@type": "Question",
                "name": "How do I choose the right quality level?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Start with 'Standard' for most use cases. Use 'Minimal' for quick drafts, 'Detailed' for production models, and 'Exhaustive' when you need every possible detail."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="hero-section relative flex flex-col items-center justify-center text-center px-6 py-8 bg-gradient-to-br from-surface to-background overflow-hidden" style={{ minHeight: '500px' }}>
        <div className="max-w-4xl w-full" style={{ minHeight: '120px' }}>
          <h1 className="text-4xl md:text-6xl font-bold mb-2 text-primaryText" style={{ lineHeight: '1.1', minHeight: '1.1em' }} suppressHydrationWarning>
            Transform Your Images into Perfect LoRA Training Data
          </h1>
        </div>
        <div className="max-w-2xl w-full" style={{ minHeight: '60px' }}>
          <p className="text-secondaryText text-lg md:text-xl mb-6" suppressHydrationWarning>
            We will literally save you HOURS with AI-powered captioning built for LoRA creators
          </p>
        </div>
        <form onSubmit={handleGetStarted} className="w-full max-w-md flex gap-2">
          <input
            type="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg bg-button border border-border text-primaryText placeholder-secondaryText focus:border-accent focus:outline-none"
          />
          <button type="submit" className="btn-primary px-6 py-2">
            Free Trial
          </button>
        </form>
        <p className="text-sm text-secondaryText mt-2">No traps. No tricks. No obligations. Free trial.</p>
        
        {/* Demo visualization */}
        <div className="mt-6 grid md:grid-cols-2 gap-8 max-w-5xl w-full" style={{ minHeight: '400px' }}>
          <div className="text-left">
            <h4 className="text-primaryText mb-3 font-semibold">Sample Image</h4>
            <div className="bg-surface rounded-lg p-6 border border-border">
              <div className="w-full h-64 bg-gray-100 rounded mb-4 flex items-center justify-center">
                <img 
                  src="/sample-holiday.webp" 
                  alt="Woman in red sweater with Santa hat" 
                  className="max-w-full max-h-full object-contain"
                  loading="eager"
                  width="400"
                  height="256"
                  style={{ aspectRatio: '400/256' }}
                />
              </div>
              <p className="text-secondaryText text-sm">Upload your images and get instant, detailed captions</p>
            </div>
          </div>
          <div className="text-left">
            <h4 className="text-primaryText mb-3 font-semibold">AI-Generated Tags</h4>
            <div className="bg-surface rounded-lg p-6 border border-accent">
              <div className="w-full h-64 overflow-y-auto bg-button rounded mb-4 p-4 text-left">
                <p className="text-xs text-accent font-mono leading-relaxed">
                  photograph, 1girl, long brown hair, wavy hair, brown eyes, adult, front view, facing forward, winking, smiling, red knit sweater, Santa hat, red lipstick, hand on hip, arm raised, indoors, plain gray background, soft lighting, portrait, vibrant colors, festive mood, holiday theme, casual clothing, medium close-up, cheerful expression, neutral background, winter fashion, curly hairstyle, playful pose, Christmas accessory, red nail polish, holiday attire, knit texture, white fur trim, relaxed posture, expressive face, joyful mood, seasonal clothing, confident stance, gesture with one hand, cropped image, clear focus, detailed features, minimalistic setting, simple composition, cozy look.
                </p>
              </div>
              <p className="text-accent text-sm font-semibold">Generated in seconds with OpenAI Vision!</p>
            </div>
          </div>
        </div>
        
        <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-accent/5 blur-3xl pointer-events-none" />
      </section>

      {/* Problem/Solution */}
      <section className="py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-primaryText mb-4">The Problem</h2>
              <p className="text-secondaryText mb-6">
                Creating high-quality captions for LoRA training is tedious. Each image needs detailed descriptions that capture every important element - from character features to artistic style.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-secondaryText">Manual captioning takes 5-10 minutes per image</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-secondaryText">Inconsistent quality across your dataset</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">✗</span>
                  <span className="text-secondaryText">Easy to miss important details</span>
                </li>
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-primaryText mb-4">Our Solution</h2>
              <p className="text-secondaryText mb-6">
                LoRA Tag uses OpenAI Vision to analyze your images and generate perfect captions instantly. Get consistent, detailed descriptions tailored for your specific training needs.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span className="text-secondaryText">Generate captions in seconds, not minutes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span className="text-secondaryText">Consistent quality across all images</span>
                </li>
                <li className="flex items-start">
                  <span className="text-accent mr-2">✓</span>
                  <span className="text-secondaryText">Never miss important visual elements</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-8 px-6 bg-surface">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primaryText mb-6">
            Everything You Need for Perfect Training Data
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Batch Processing",
                description: "Upload up to 100 images at once. Process entire datasets in minutes, not hours.",
                icon: "M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
              },
              {
                title: "Quality Levels",
                description: "From quick drafts to exhaustive descriptions. Choose the detail level that fits your needs.",
                icon: "M13 10V3L4 14h7v7l9-11h-7z"
              },
              {
                title: "Multiple LoRA Types",
                description: "Optimized prompts for Character, Style, Object, and Concept LoRAs.",
                icon: "M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"
              },
              {
                title: "Smart Tagging",
                description: "Automatically identifies characters, styles, poses, and environments.",
                icon: "M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              },
              {
                title: "Instant Downloads",
                description: "Get organized .txt files paired with your images, ready for training.",
                icon: "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
              },
              {
                title: "Flexible Export",
                description: "Multiple format options to match any training workflow or preference.",
                icon: "M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
              }
            ].map((feature, idx) => (
              <div key={idx} className="bg-background rounded-lg p-6 border border-border hover:border-accent transition-colors">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={feature.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-primaryText mb-2">{feature.title}</h3>
                <p className="text-secondaryText">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primaryText mb-6">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Upload Your Images",
                description: "Drag & drop or select folders. We support JPG, PNG, WebP, and more."
              },
              {
                step: "2",
                title: "Choose Your Settings",
                description: "Select LoRA type and quality level. Add global tags if needed."
              },
              {
                step: "3",
                title: "Download Your Dataset",
                description: "Get perfectly formatted caption files ready for training."
              }
            ].map((item, idx) => (
              <div key={idx} className="relative">
                <div className="text-center">
                  <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-primaryText mb-2">{item.title}</h3>
                  <p className="text-secondaryText">{item.description}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-8 left-full w-full">
                    <svg className="w-full h-2 text-accent/30" viewBox="0 0 100 8">
                      <path d="M0,4 L90,4" stroke="currentColor" strokeWidth="2" strokeDasharray="5,5" />
                      <path d="M90,4 L100,4 L95,0 M100,4 L95,8" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-8 px-6 bg-surface">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primaryText mb-4">
            Simple, Transparent Pricing
          </h2>
          <p className="text-center text-secondaryText mb-6 max-w-2xl mx-auto">
            Start free, upgrade when you need more. All plans include core features.
          </p>
          <div className="grid md:grid-cols-5 gap-6">
            {[
              {
                name: "Free",
                price: "$0",
                period: "/month",
                images: "10 images",
                features: ["Standard quality", "Perfect for trying it out"],
                cta: "Free Trial",
                popular: false
              },
              {
                name: "Starter",
                price: "$6.99",
                period: "/month",
                images: "600 images",
                features: ["Standard quality", "Email support", "Top-up: $5/600 images"],
                badge: "20% savings",
                cta: "Get Started",
                popular: false
              },
              {
                name: "Professional",
                price: "$10.99",
                period: "/month",
                images: "1,200 images",
                features: ["Up to detailed quality", "Priority processing", "Top-up: $8/1,200"],
                badge: "Most Popular",
                cta: "Get Started",
                popular: true
              },
              {
                name: "Business",
                price: "$24.99",
                period: "/month",
                images: "3,000 images",
                features: ["All quality levels", "Bulk processing", "Custom tags", "Top-up: $20/3,000"],
                badge: "Best Value",
                cta: "Get Started",
                popular: false
              },
              {
                name: "Enterprise",
                price: "$69.99",
                period: "/month",
                images: "10,000 images",
                features: ["White glove support", "Custom integrations", "Dedicated manager"],
                cta: "Contact Us",
                popular: false
              }
            ].map((plan, idx) => (
              <div key={idx} className={`relative rounded-lg p-6 ${plan.popular ? 'bg-gradient-to-b from-accent/20 to-accent/10 border-2 border-accent' : 'bg-background border border-border'}`}>
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 text-xs font-semibold rounded-full ${plan.popular ? 'bg-accent text-white' : 'bg-blue-500 text-white'}`}>
                    {plan.badge}
                  </div>
                )}
                <h3 className="text-xl font-semibold text-primaryText mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-primaryText">{plan.price}</span>
                  <span className="text-secondaryText">{plan.period}</span>
                </div>
                <p className="text-accent font-semibold mb-4">{plan.images}/month</p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((feature, fidx) => (
                    <li key={fidx} className="flex items-start">
                      <Check className="w-4 h-4 text-accent mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-secondaryText">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => navigate('/account?mode=signup')}
                  className={`w-full py-2 rounded-lg font-semibold transition-colors ${plan.popular ? 'bg-accent text-white hover:bg-accent/90' : 'bg-button text-primaryText hover:bg-button/80 border border-border'}`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="py-8 px-6 bg-surface">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-primaryText mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {[
              {
                q: "What file formats are supported?",
                a: "We support all common image formats including JPG, PNG, WebP, GIF, and BMP. Files up to 10MB each (higher limits on paid plans)."
              },
              {
                q: "How accurate are the captions?",
                a: "Our OpenAI Vision integration provides highly accurate descriptions. The AI identifies characters, poses, clothing, environments, and artistic styles with impressive precision."
              },
              {
                q: "Can I edit captions after generation?",
                a: "Yes! Every caption can be edited before download. You can also regenerate individual captions if needed."
              },
              {
                q: "What's a LoRA?",
                a: <>LoRA (Low-Rank Adaptation) is a training technique for customizing AI image models. It requires a dataset of images with detailed captions describing their contents. <Link to="/lora-training" className="text-accent hover:underline">Learn more about LoRA training</Link>.</>
              },
              {
                q: "How do I choose the right quality level?",
                a: "Start with 'Standard' for most use cases. Use 'Minimal' for quick drafts, 'Detailed' for production models, and 'Exhaustive' when you need every possible detail."
              }
            ].map((faq, idx) => (
              <div key={idx} className="bg-background rounded-lg p-6 border border-border">
                <h3 className="text-lg font-semibold text-primaryText mb-2">{faq.q}</h3>
                <p className="text-secondaryText">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Resources */}
      <section className="py-8 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-accent/10 to-accent/20 rounded-lg p-8 border border-accent/30">
            <h2 className="text-2xl font-bold text-primaryText mb-4">
              New to LoRA Training?
            </h2>
            <p className="text-secondaryText mb-6">
              Learn everything you need to know about creating custom AI models with our comprehensive guide.
            </p>
            <Link 
              to="/lora-training" 
              className="inline-flex items-center gap-2 bg-accent hover:bg-accent/90 transition text-white font-semibold px-6 py-3 rounded-lg"
            >
              Learn about LoRA Training
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-8 px-6 bg-gradient-to-br from-accent/20 to-accent/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-primaryText mb-4">
            Ready to Transform Your Training Data?
          </h2>
          <p className="text-xl text-secondaryText mb-6">
            Join hundreds of creators who are building better LoRAs with less effort.
          </p>
          <form onSubmit={handleCtaSubmit} className="max-w-md mx-auto flex gap-2">
            <input
              type="email"
              placeholder="Enter your email"
              value={ctaEmail}
              onChange={(e) => setCtaEmail(e.target.value)}
              className="flex-1 px-4 py-3 rounded-lg bg-background border border-border text-primaryText placeholder-secondaryText focus:border-accent focus:outline-none"
            />
            <button type="submit" className="bg-accent hover:bg-accent/90 transition text-white font-semibold px-6 py-3 rounded-lg">
              Start Your Free Trial
            </button>
          </form>
          <p className="text-sm text-secondaryText mt-4">No traps. No tricks. No obligations. Free trial.</p>
        </div>
      </section>

      {/* Simple Footer */}
      <footer className="bg-background border-t border-border py-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-secondaryText text-sm">
            © 2024 LoRA Tag. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;